/**
 * <liquid-hero> — a full-bleed WebGL2 background that looks like a slowly
 * poured, glossy liquid (honey, oil, wine — the colors decide).
 *
 * Domain-warped simplex fBm gives a flowing height field; screen-space
 * derivatives turn it into normals for a specular sheen. The pointer stirs a
 * local swirl whose strength follows pointer speed and decays back to calm.
 *
 * Falls back to the poster image or CSS gradient when WebGL2 is unavailable,
 * and paints a single still frame when visitors prefer reduced motion.
 */

const VERTEX = `#version 300 es
in vec2 aPosition;
void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const FRAGMENT = `#version 300 es
precision highp float;

uniform vec2 uResolution;
uniform float uTime;
uniform vec2 uPointer;
uniform float uStir;
uniform vec3 uDeep;
uniform vec3 uBody;
uniform vec3 uGlint;
uniform float uWarp;
uniform float uZoom;
uniform float uBump;

out vec4 fragColor;

// 2D simplex noise — Ashima Arts / Stefan Gustavson (MIT)
vec3 permute(vec3 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

const mat2 OCTAVE = mat2(1.6, 1.2, -1.2, 1.6);

float fbm(vec2 p, int octaves) {
  float value = 0.0;
  float amplitude = 0.5;
  for (int i = 0; i < 5; i++) {
    if (i >= octaves) break;
    value += amplitude * snoise(p);
    p = OCTAVE * p;
    amplitude *= 0.5;
  }
  return value;
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y * uZoom;
  vec2 pointer = (uPointer - 0.5 * uResolution) / uResolution.y * uZoom;

  // Stir: rotate space around the pointer, strongest at its center.
  vec2 fromPointer = p - pointer;
  float falloff = exp(-dot(fromPointer, fromPointer) * 2.2 / (uZoom * uZoom));
  float angle = uStir * falloff * 2.6;
  p = pointer + mat2(cos(angle), -sin(angle), sin(angle), cos(angle)) * fromPointer;

  float t = uTime;
  // Low-octave warp fields keep the folds broad and viscous.
  vec2 q = vec2(
    fbm(p + vec2(0.0, 0.08 * t), 2),
    fbm(p + vec2(5.2, 1.3) - 0.06 * t, 2)
  );
  vec2 r = vec2(
    fbm(p + uWarp * q + vec2(1.7, 9.2) + 0.09 * t, 3),
    fbm(p + uWarp * q + vec2(8.3, 2.8) - 0.07 * t, 3)
  );
  float height = fbm(p + uWarp * r, 3);

  // Glossy shading from the height field's screen-space slope.
  vec2 slope = vec2(dFdx(height), dFdy(height)) * uResolution.y / uZoom;
  vec3 normal = normalize(vec3(-slope * uBump, 1.0));
  vec2 lightDrift = (uPointer / uResolution - 0.5) * 0.5;
  vec3 light = normalize(vec3(-0.45 + lightDrift.x, 0.6 + lightDrift.y, 0.8));
  vec3 halfway = normalize(light + vec3(0.0, 0.0, 1.0));

  float diffuse = clamp(dot(normal, light), 0.0, 1.0);
  float specular = pow(max(dot(normal, halfway), 0.0), 22.0);
  float sheen = pow(1.0 - normal.z, 1.5);

  float body = smoothstep(-0.55, 0.55, height + 0.3 * length(q) - 0.1);
  vec3 color = mix(uDeep, uBody, body);
  color *= 0.6 + 0.6 * diffuse;
  // Light scattering through the thicker parts of the pour.
  color += uBody * 0.45 * smoothstep(0.35, 1.0, body) * (0.6 + 0.4 * diffuse);
  color = mix(color, uGlint, smoothstep(0.6, 1.1, length(r)) * 0.18);
  color += uGlint * (specular * 0.55 + sheen * 0.2) * smoothstep(-0.35, 0.45, height);

  // Vignette + dithering to keep dark gradients free of banding.
  vec2 centered = uv - 0.5;
  color *= 1.0 - dot(centered, centered) * 0.9;
  color += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;

  fragColor = vec4(color, 1.0);
}`;

const MAX_PIXELS = 1.2e6;

function hexToRgb(hex) {
  const value = parseInt(hex.replace('#', '').slice(0, 6), 16);
  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
}

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn('[liquid-hero]', gl.getShaderInfoLog(shader));
    return null;
  }
  return shader;
}

class LiquidHero extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    if (!this.canvas || this.dataset.mode !== 'shader') return;

    const gl = this.canvas.getContext('webgl2', { antialias: false, alpha: false, powerPreference: 'low-power' });
    if (!gl || !this.setup(gl)) {
      this.classList.add('is-fallback');
      return;
    }

    this.animate = window.Theme?.motion() ?? true;
    this.time = 20; // Start mid-flow so the first frame is already interesting.
    this.speed = Number(this.dataset.speed || 1);
    this.pointer = { x: 0, y: 0 };
    this.target = { x: 0, y: 0 };
    this.stir = 0;
    this.visible = true;

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this);
    this.resize();
    this.pointer = { ...this.target };

    this.canvas.addEventListener('webglcontextlost', this.onContextLost);

    if (!this.animate) {
      this.render(0);
      this.classList.add('is-live');
      return;
    }

    this.addEventListener('pointermove', this.onPointerMove);
    this.intersection = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) this.start();
    });
    this.intersection.observe(this);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.start();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.resizeObserver?.disconnect();
    this.intersection?.disconnect();
    this.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.gl?.getExtension('WEBGL_lose_context')?.loseContext();
  }

  setup(gl) {
    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    if (!vertex || !fragment) return false;

    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.warn('[liquid-hero]', gl.getProgramInfoLog(program));
      return false;
    }
    gl.useProgram(program);

    // One oversized triangle covers the viewport.
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const uniform = (name) => gl.getUniformLocation(program, name);
    this.uniforms = {
      resolution: uniform('uResolution'),
      time: uniform('uTime'),
      pointer: uniform('uPointer'),
      stir: uniform('uStir'),
    };
    gl.uniform3fv(uniform('uDeep'), hexToRgb(this.dataset.deep || '#0b0906'));
    gl.uniform3fv(uniform('uBody'), hexToRgb(this.dataset.body || '#6b3f0a'));
    gl.uniform3fv(uniform('uGlint'), hexToRgb(this.dataset.glint || '#f6cf7e'));
    gl.uniform1f(uniform('uWarp'), Number(this.dataset.warp || 1.5));
    gl.uniform1f(uniform('uZoom'), Number(this.dataset.zoom || 1));
    gl.uniform1f(uniform('uBump'), Number(this.dataset.bump || 0.1));

    this.gl = gl;
    return true;
  }

  resize() {
    const rect = this.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const scale = Math.min(dpr, Math.sqrt(MAX_PIXELS / Math.max(1, rect.width * rect.height)));
    const width = Math.max(1, Math.round(rect.width * scale));
    const height = Math.max(1, Math.round(rect.height * scale));

    // Resting pointer position: upper right, where the pour gathers.
    const rest = { x: width * 0.68, y: height * 0.62 };
    if (!this.hasPointer) this.target = rest;

    if (this.canvas.width === width && this.canvas.height === height) return;
    this.canvas.width = width;
    this.canvas.height = height;
    this.gl.viewport(0, 0, width, height);
    this.gl.uniform2f(this.uniforms.resolution, width, height);
    if (!this.animate) this.render(0);
  }

  onPointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    const rect = this.getBoundingClientRect();
    const scale = this.canvas.width / rect.width;
    const x = (event.clientX - rect.left) * scale;
    const y = (rect.bottom - event.clientY) * scale;
    const distance = Math.hypot(x - this.target.x, y - this.target.y) / this.canvas.height;
    this.stir = Math.min(1, this.stir + distance * 2.2);
    this.target = { x, y };
    this.hasPointer = true;
  };

  onVisibility = () => {
    if (!document.hidden) this.start();
  };

  onContextLost = (event) => {
    event.preventDefault();
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.classList.remove('is-live');
    this.classList.add('is-fallback');
  };

  start() {
    if (this.frame || !this.gl) return;
    this.last = performance.now();
    this.frame = requestAnimationFrame(this.tick);
  }

  tick = (now) => {
    if (!this.visible || document.hidden) {
      this.frame = null;
      return;
    }
    const delta = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    this.render(delta);
    if (!this.classList.contains('is-live')) this.classList.add('is-live');
    this.frame = requestAnimationFrame(this.tick);
  };

  render(delta) {
    const { gl, uniforms } = this;
    const ease = 1 - Math.pow(0.001, delta);
    this.time += delta * this.speed;
    this.pointer.x += (this.target.x - this.pointer.x) * ease;
    this.pointer.y += (this.target.y - this.pointer.y) * ease;
    this.stir *= Math.pow(0.35, delta);

    gl.uniform1f(uniforms.time, this.time);
    gl.uniform2f(uniforms.pointer, this.pointer.x, this.pointer.y);
    gl.uniform1f(uniforms.stir, this.stir);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
}

if (!customElements.get('liquid-hero')) customElements.define('liquid-hero', LiquidHero);
